import { useTranslation } from 'react-i18next';
import React from 'react';
import './StepGameMode.css';

const MODES = [
  { key: 'competitive', label: 'Competitive', icon: '🏆', descKey: 'createMatch.modeCompetitive' },
  { key: 'wingman', label: 'Wingman', icon: '👥', descKey: 'createMatch.modeWingman' },
  { key: 'duel', label: 'Duel', icon: '⚔️', descKey: 'createMatch.modeDuel' },
];

const WEAPON_MODES = [
  { key: 'ak', label: 'AK-47', icon: '🔫' },
  { key: 'm4a4', label: 'M4A4', icon: '🔫' },
  { key: 'm4a1s', label: 'M4A1-S', icon: '🔫' },
  { key: 'deagle', label: 'Desert Eagle', icon: '🔫' },
  { key: 'awp', label: 'AWP', icon: '🎯' },
];

/**
 * Game mode selection step with optional weapon mode for duels.
 *
 * @param {Object} props
 * @param {Object} props.match - Match object with team data
 * @param {string} props.gameMode - Currently selected game mode
 * @param {string} props.weaponMode - Currently selected weapon mode (duel only)
 * @param {Function} props.onSelect - Callback when game mode is selected
 * @param {Function} props.onWeaponSelect - Callback when weapon mode is selected
 * @param {Function} props.onBack - Callback for back button
 * @param {Function} props.onNext - Callback for next button
 */
export default function StepGameMode({ match, gameMode, weaponMode, onSelect, onWeaponSelect, onBack, onNext }) {
  const { t } = useTranslation();
  const playerCount = (match?.team1?.players?.length || 0) + (match?.team2?.players?.length || 0);

  const availableModes = MODES.filter(m => {
    if (playerCount <= 2) return m.key === 'duel';
    if (playerCount <= 4) return m.key === 'competitive' || m.key === 'wingman';
    return m.key === 'competitive';
  });

  const showWeaponPicker = gameMode === 'duel';

  return (
    <div className="step-content">
      <h2>{t('createMatch.selectGameMode')}</h2>
      <p className="game-mode-desc">
        {t('createMatch.playerCountSuffix', { count: playerCount })} {availableModes.length === 1
          ? t('createMatch.modeAuto', { mode: availableModes[0].label })
          : t('createMatch.chooseModeForMatch')}
      </p>

      <div className="game-mode-grid">
        {availableModes.map(mode => (
          <div
            key={mode.key}
            className={`game-mode-card ${gameMode === mode.key ? 'selected' : ''}`}
            onClick={() => onSelect(mode.key)}
          >
            <span className="game-mode-icon">{mode.icon}</span>
            <span className="game-mode-label">{mode.label}</span>
            <span className="game-mode-detail">{t(mode.descKey)}</span>
          </div>
        ))}
      </div>

      {showWeaponPicker && (
        <>
          <h3 className="weapon-mode-title">{t('createMatch.selectWeapon')}</h3>
          <div className="weapon-mode-grid">
            {WEAPON_MODES.map(w => (
              <div
                key={w.key}
                className={`weapon-mode-card ${weaponMode === w.key ? 'selected' : ''}`}
                onClick={() => onWeaponSelect(w.key)}
              >
                <span className="weapon-mode-label">{w.label}</span>
              </div>
            ))}
          </div>
        </>
      )}

      <div className="step-nav">
        <button className="back-btn" onClick={onBack}>{t('profile.back')}</button>
        <button className="next-btn" onClick={onNext} disabled={!gameMode || (showWeaponPicker && !weaponMode)}>{t('createMatch.next')}</button>
      </div>
    </div>
  );
}
